import classNames from 'classnames'
import { renderEntry } from 'marketing-site/lib/render'
import { WithAdditionalCTALinkOverrideProps } from 'marketing-site/src/components/context/CTALinkOverridePropsContext'
import { SwitcheableContent } from 'marketing-site/src/library/elements/SwitcheableContent'
import { Text } from 'marketing-site/src/library/elements/Text'
import circleCheckmark from 'marketing-site/src/library/images/circleCheckmark.svg'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import { HeroAnimation } from '../../elements/HeroAnimation'
import { RichText } from '../../elements/RichText'
import useTranslate from '../../hooks/useTranslate'
import { ModeOptions, modes, useModeToggle } from '../../providers/toggleProvider'
import { fontSmileScript, mq } from '../../utils'
import { CTATheme } from '../../utils/constants/themes-legacy'
import { GatedContent } from '../GatedContent'
import { ToggleMenu } from '../ToggleMenu'
import { WiredCardsComponent } from '../WiredCardsComponent'
import { IProps, ISwitchableChildren } from './index'

import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { HeroWithToggle as HeroWithToggleDefault } from './component'

export const HeroWithToggle = (props: IProps) => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <HeroWithToggleDefault {...props} />
  }
  return <HeroWithToggleLegacy {...props} />
}

const HeroWithToggleLegacy: React.FC<IProps> = ({
  customerHeadline,
  teamsHeadline,
  customerImage,
  customerAnimation,
  customerMobileImage,
  customerMobileAnimation,
  teamsImage,
  teamsAnimation,
  teamsMobileImage,
  teamsMobileAnimation,
  customerDescription,
  teamsDescription,
  customerList,
  teamList,
  wiredCards,
  switchableSupportSpotlights,
  gatedContent,
  renderPrimaryCta,
  renderSecondaryCta,
}) => {
  const { currentMode, mode } = useModeToggle()

  const { ref, inView } = useInView({ threshold: 0.05, initialInView: true })

  const { translate } = useTranslate()

  const isTeamsModeActive = currentMode === ModeOptions.teams

  const backgroundClasses = classNames('hero-with-toggle-background-customer', {
    'hero-with-toggle-background-customer-off': isTeamsModeActive,
  })

  const customerClasses = classNames('theme', {
    'theme--opacity-off': isTeamsModeActive,
  })

  const teamsClasses = classNames('theme', {
    'theme--opacity-off': !isTeamsModeActive,
  })

  const renderSwitchableChildren = (children: ISwitchableChildren, index: number) => {
    Object.assign(children.componentA.fields, { isVisible: !isTeamsModeActive })

    const childA = renderEntry(children.componentA)
    if (!children.componentB) {
      return <>{childA}</>
    }

    Object.assign(children.componentB.fields, { isVisible: isTeamsModeActive })

    const childB = renderEntry(children.componentB)
    return (
      <SwitcheableContent
        key={index}
        childA={childA}
        childB={childB}
        experience={!isTeamsModeActive ? 'A' : 'B'}
      />
    )
  }

  const supportSpotlightGradiants = isTeamsModeActive
    ? 'radial-gradient(50% 50% at 50% 50%, rgba(50, 107, 255, 0.4) 0%, rgba(50, 107, 255, 0) 100%)'
    : 'radial-gradient(50% 50% at 50% 50%, #F5F5F5 0%, rgba(245, 245, 245, 0) 100%)'

  return (
    <>
      <div className={`hero-with-toggle ${backgroundClasses}`}>
        <div className={`hero-with-toggle--size`} ref={ref}>
          <div className={`hwt-main`}>
            <div className={`hwt-main__desktop-content`}>
              <div className={`hwt-headline-container`}>
                <div className={`hwt-headline-container__handwritten ${customerClasses}`}>
                  <Text size="xxl+">{customerHeadline}</Text>
                </div>
                <div className={`hwt-headline-container__handwritten ${teamsClasses}`}>
                  <Text size="xxl+">{teamsHeadline}</Text>
                </div>
              </div>

              <div className="hwt-container">
                <div className="hwt-container__body-content">
                  <div className={`hwt-container__description`}>
                    <p className={`hwt-container__description-ct ${customerClasses}`}>
                      {customerDescription}
                    </p>
                    <p className={`hwt-container__description-ct ${teamsClasses}`}>
                      {teamsDescription}
                    </p>
                  </div>

                  <div className={`hwt-container__list`}>
                    {customerList && (
                      <div className={`hwt-container__list-ct ${customerClasses}`}>
                        <RichText html={customerList} />
                      </div>
                    )}
                    {teamList && (
                      <div className={`hwt-container__list-ct ${teamsClasses}`}>
                        <RichText html={teamList} />
                      </div>
                    )}
                  </div>
                  <div className={`hwt-container__cta`}>
                    <WithAdditionalCTALinkOverrideProps
                      overrideProps={{ bgColor: CTATheme.BlackFill }}
                    >
                      {renderPrimaryCta()}
                    </WithAdditionalCTALinkOverrideProps>
                    {renderSecondaryCta?.()}
                  </div>
                </div>
                <HeroAnimation
                  firstImage={customerImage}
                  firstAnimation={customerAnimation}
                  firstMobileImage={customerMobileImage}
                  firstMobileAnimation={customerMobileAnimation}
                  secondAnimation={teamsAnimation}
                  secondImage={teamsImage}
                  secondMobileAnimation={teamsMobileAnimation}
                  secondMobileImage={teamsMobileImage}
                  teamMode={isTeamsModeActive}
                />
              </div>
            </div>
          </div>

          <div className="hwt__wired-cards">
            <WiredCardsComponent {...wiredCards} />
          </div>
          <div className="hero-with-toggle__dynamic-content">
            {switchableSupportSpotlights.map(renderSwitchableChildren)}
          </div>
          <div className="hwt__gated-content">
            {gatedContent && <GatedContent {...gatedContent} headingType={'Graphik'} />}
          </div>
        </div>
        <ToggleMenu
          leftText={translate('Customer view')}
          rightText={translate('Support team view')}
        />
      </div>
      <style jsx>{`
        .hero-with-toggle-background-customer {
          background: ${modes.customer.background};
          opacity: 1;

          &-off {
            background: ${modes.teams.background};
            opacity: 0.99;

            .hwt-main {
              background: linear-gradient(
                0deg,
                rgba(255, 255, 255, 0),
                rgba(255, 255, 255, 0.5),
                rgba(255, 255, 255, 0.335),
                rgba(255, 255, 255, 0)
              );
            }
          }

          .hwt-container__cta {
            display: flex;
            gap: 1.5rem;
          }
        }

        :global(.hero-with-toggle__dynamic-content .support-spotlight-component) {
          background: ${supportSpotlightGradiants};
        }
      `}</style>

      <style jsx>
        {`
          .hero-with-toggle {
            padding-bottom: 180px;
            &--size {
              padding-top: 3rem;
            }
          }

          .hwt-main {
            margin-bottom: 3rem;
          }

          :global(.hero-with-toggle .toggle-menu) {
            box-shadow: ${mode.headerShadow ?? 'none'};
          }

          .theme {
            transition: opacity 800ms;
            opacity: 1;
            &--opacity-off {
              opacity: 0;
              animation-name: hero-with-toggle-fade-out;
              animation-delay: 800ms;
              animation-duration: 1ms;
              animation-fill-mode: forwards;
            }
          }

          .hwt-headline-container {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            margin-bottom: 34px;

            &__handwritten {
              grid-row-start: 1;
              grid-column-start: 1;

              text-align: center;
              padding: 0 31px;
              :global(span > span) {
                font-family: ${fontSmileScript};
                font-weight: normal;
                font-size: 36px;
                font-feature-settings: 'liga' 1;
              }
            }
          }

          .hwt-container {
            display: flex;
            flex-direction: column-reverse;
            margin-bottom: 40px;

            &__description {
              margin-top: 2rem;
              font-size: 18px;
              line-height: 24px;
              display: grid;
              grid-template-columns: 1fr;
              grid-template-rows: 1fr;
              &-ct {
                grid-row-start: 1;
                grid-column-start: 1;
                text-align: center;
                padding: 0px 31px;
              }
            }

            &__list {
              display: grid;
              grid-template-columns: 1fr;
              grid-template-rows: 1fr;
              &-ct {
                grid-row-start: 1;
                grid-column-start: 1;
                padding: 0 31px;
                :global(.rich-text > p) {
                  margin-top: 2rem;
                  text-align: center;
                  font-family: $fontGraphik;
                }
                :global(li::before) {
                  background-image: url(${circleCheckmark});
                }
                :global(.rich-text ul) {
                  columns: 2;
                }
                :global(.rich-text li) {
                  display: inline-block;
                  width: 100%;
                }
              }
            }

            &__cta {
              display: flex;
              justify-content: center;
            }
          }

          :global(.hero-with-toggle .toggle-menu) {
            transform: ${inView ? 'translateY(0%)' : 'translateY(-100%)'};
            transition: transform 200ms;
          }

          @media (${mq.laptop}) {
            .hwt-headline-container {
              &__handwritten {
                padding: 0 64px;
                :global(span > span) {
                  font-size: 48px;
                  font-weight: 800;
                }
              }
            }
            .hwt-container {
              display: flex;
              flex-direction: row;
              padding: 0 40px;
              background-repeat: no-repeat;
              background-size: 500px 430px;
              background-position: 260px 20px;

              .hwt-container__body-content {
                flex: 1 1 40%;
              }

              &__description {
                margin-top: 0;
                &-ct {
                  text-align: left;
                  padding: 0px;
                }
              }
              &__list {
                &-ct {
                  padding: 0;
                  :global(.rich-text > p) {
                    margin-top: 2rem;
                    text-align: left;
                  }
                }
              }

              &__cta {
                justify-content: flex-start;
              }
            }
          }

          @media (${mq.desktop}) {
            .hwt-main {
              margin-bottom: 7.25rem;
              &__desktop-content {
                padding: 0 60px;
              }
            }
            .hwt-headline-container {
              max-width: 780px;
              &__handwritten {
                padding: 0;
                text-align: left;
              }
            }
            .hwt-container {
              display: flex;
              padding: 0px;
              background-size: 620px 440px;
              background-position: 310px 50px;
              &__list {
                margin-bottom: 1rem;
                &-ct {
                  :global(.rich-text ul) {
                    columns: 1;
                  }
                }
              }
            }
          }

          @media (${mq.desktopLg}) {
            .hwt-container__body-content {
              flex: 1 1 25%;
            }
            .hero-with-toggle {
              padding-bottom: 105px;
            }
            .hwt-container {
              background-image: unset;
            }
            .hwt-main {
              display: flex;
              justify-content: center;
              &__desktop-content {
                width: 1440px;
                padding: 0 60px;
              }
            }
          }

          @keyframes hero-with-toggle-fade-out {
            from {
              visibility: visible;
            }
            to {
              visibility: hidden;
            }
          }
        `}
      </style>
    </>
  )
}
